/*
Copyright (c) 2015, salesforce.com, inc. All rights reserved.

Redistribution and use in source and binary forms, with or without modification, are permitted provided that the following conditions are met:
Redistributions of source code must retain the above copyright notice, this list of conditions and the following disclaimer.
Redistributions in binary form must reproduce the above copyright notice, this list of conditions and the following disclaimer in the documentation and/or other materials provided with the distribution.
Neither the name of salesforce.com, inc. nor the names of its contributors may be used to endorse or promote products derived from this software without specific prior written permission.

THIS SOFTWARE IS PROVIDED BY THE COPYRIGHT HOLDERS AND CONTRIBUTORS "AS IS" AND ANY EXPRESS OR IMPLIED WARRANTIES, INCLUDING, BUT NOT LIMITED TO, THE IMPLIED WARRANTIES OF MERCHANTABILITY AND FITNESS FOR A PARTICULAR PURPOSE ARE DISCLAIMED. IN NO EVENT SHALL THE COPYRIGHT OWNER OR CONTRIBUTORS BE LIABLE FOR ANY DIRECT, INDIRECT, INCIDENTAL, SPECIAL, EXEMPLARY, OR CONSEQUENTIAL DAMAGES (INCLUDING, BUT NOT LIMITED TO, PROCUREMENT OF SUBSTITUTE GOODS OR SERVICES; LOSS OF USE, DATA, OR PROFITS; OR BUSINESS INTERRUPTION) HOWEVER CAUSED AND ON ANY THEORY OF LIABILITY, WHETHER IN CONTRACT, STRICT LIABILITY, OR TORT (INCLUDING NEGLIGENCE OR OTHERWISE) ARISING IN ANY WAY OUT OF THE USE OF THIS SOFTWARE, EVEN IF ADVISED OF THE POSSIBILITY OF SUCH DAMAGE.
*/
/* eslint-disable-next-line jest/no-mocks-import */
import { mockProjectedEvents, mockCalendarEvents } from '../../__mocks__/events';

import { GET_CALENDAR_EVENTS, GET_PROJECTED_SENDS } from '../constants';

import { getFullDateString } from '../utilities/dates';
import { removeDuplicates } from '../utilities/general';

function getLocalDate(dateString) {
  const parts = dateString.split('-');
  return new Date(parts[0], parts[1] - 1, parts[2]);
}

function getInstanceDates(events = []) {
  return events.map((event) => {
    if (!event.instanceDates) {
      if (!event.isAllDay && event.localInstanceTimes) {
        // handle the projected events as pre-calculated points in time
        event.instanceDates = event.localInstanceTimes.map((time) => time.split('T')[0]);
      } else if (event.isAllDay && event.localStartTime) {
        // handle events as single or multi day
        const localStartDate = event.localStartTime.split('T')[0];
        event.instanceDates = [localStartDate];

        if (event.localEndTime) {
          const localEndDate = event.localEndTime.split('T')[0];
          const date = getLocalDate(localStartDate);
          const endDate = getLocalDate(localEndDate);

          while (date.getTime() < endDate.getTime()) {
            date.setDate(date.getDate() + 1);
            event.instanceDates.push(getFullDateString(date));
          }
        }
      }
    }

    return event;
  });
}

function cleanData(events) {
  if (events) {
    return getInstanceDates(
      events.map(({ id, isAllDay, type, title, instanceTimes, localInstanceTimes, localStartTime, localEndTime }) => ({
        id,
        isAllDay,
        type,
        title,
        instanceTimes,
        localInstanceTimes,
        localStartTime,
        localEndTime,
      }))
    );
  }

  return events;
}

function cleanProjectedData(events) {
  if (events) {
    return getInstanceDates(
      events.map(({ automationId, type, title, instanceTimes, localInstanceTimes, localStartTime, localEndTime }) => ({
        id: automationId,
        isAllDay: false,
        type,
        title,
        instanceTimes,
        localInstanceTimes,
        localStartTime,
        localEndTime,
      }))
    );
  }

  return events;
}

let exampleState = {
  projectedEventsPage: 1,
  projectedEventsPageSize: 50,
  items: [],
};
if (process.env.NODE_ENV !== 'production') {
  const cleanProjectedEvents = cleanProjectedData(mockProjectedEvents);
  const cleanCalendarEvents = cleanProjectedData(mockCalendarEvents);
  exampleState = {
    projectedEventsPage: 1,
    projectedEventsPageSize: 50,
    items: [...cleanProjectedEvents, ...cleanCalendarEvents],
  };
}

export default function calendar(state = exampleState, action) {
  switch (action.type) {
    case GET_PROJECTED_SENDS: {
      let newState = state;

      if (action.error) {
        newState = [...state];
        newState.error = action.error;
      } else if (action.payload.response && action.payload.response.items) {
        const totalPages = Math.ceil(action.payload.response.count / newState.projectedEventsPageSize);
        if (newState.projectedEventsPage < totalPages) {
          newState.projectedEventsPage += 1;
        }
        if (newState.items.length) {
          newState.items = removeDuplicates(
            newState.items.concat(cleanProjectedData(action.payload.response.items.reverse())),
            'id'
          );
        } else {
          newState.items = cleanProjectedData(action.payload.response.items.reverse());
        }
      }

      return newState;
    }
    case GET_CALENDAR_EVENTS: {
      let newState = state;

      if (action.error) {
        newState = [...state];
        newState.error = action.error;
      } else if (action.payload.response && action.payload.response.items) {
        if (newState.items.length) {
          newState.items = removeDuplicates(
            newState.items.concat(cleanData(action.payload.response.items.reverse())),
            'id'
          );
        } else {
          newState.items = cleanData(action.payload.response.items.reverse());
        }
      }

      return newState;
    }
    default: {
      return state;
    }
  }
}
